import { SECTION_IDS, type Place, type SectionId } from './place';
import { AVAILABLE_SECTIONS } from './scopes';

const SECTION_SET: ReadonlySet<string> = new Set(SECTION_IDS);

/**
 * The place and section the current address belongs to, or `null` outside the new area.
 *
 * A tenant address belongs to the area when its next segment is a section that is built;
 * `/admin/tenants/:tenantId` alone, the retired operator page's address (FR-AD-12), only
 * redirects to the tenant's Organizzazioni.
 */
export function placeFromPath(
  pathname: string,
  available: ReadonlySet<SectionId> = AVAILABLE_SECTIONS,
): { readonly place: Place; readonly section: SectionId | null } | null {
  const segments = pathname.split('/').filter((segment) => segment !== '');
  if (segments[0] !== 'admin') {
    return null;
  }
  const [, kind, id, next] = segments;
  const section = next !== undefined && SECTION_SET.has(next) ? (next as SectionId) : null;
  if (kind === 'platform') {
    return {
      place: { kind: 'platform' },
      section: id !== undefined && SECTION_SET.has(id) ? (id as SectionId) : null,
    };
  }
  if (id === undefined || id === '') {
    return null;
  }
  const decoded = decodedSegment(id);
  if (decoded === null) {
    return null;
  }
  if (kind === 'organizations') {
    return { place: { kind: 'organization', id: decoded }, section };
  }
  if (kind === 'tenants' && section !== null && available.has(section)) {
    return { place: { kind: 'tenant', id: decoded }, section };
  }
  return null;
}

/**
 * A malformed escape (`%`, `%zz`) names no place, so the address is simply not one of the area's:
 * this parser runs from the shell on every address, and must not throw there.
 */
function decodedSegment(segment: string): string | null {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}
